import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { SeoButtonComponent } from 'dibs-seo/exports/SeoButton.tsx';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import Close from 'dibs-icons/exports/Close.ts';
import { useQuickView } from './hooks/useQuickView.ts';
import { QuickViewCreators } from './QuickViewCreators.tsx';
import { QuickViewCategory } from './QuickViewCategory.tsx';
import { type TileSize } from '../constants.ts';
import { withBuyerHost } from '../helpers/withBuyerHost.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { type QuickView_item$key } from './__generated__/QuickView_item.graphql.ts';
import { type QuickView_itemSearch$key } from './__generated__/QuickView_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment QuickView_itemSearch on ItemSearchQueryConnection {
        ...QuickViewCategory_itemSearch
    }
`;

const itemFragment = graphql`
    fragment QuickView_item on Item {
        title
        linkData {
            path
        }
        quickViewDisplay {
            #### "title" and "mobileTitle" are used for non SB tile Quick View
            # title
            # mobileTitle: title(formatType: MOBILE)
            paragraphs {
                key
                text
                mobileText: text(formatType: MOBILE)
            }
            ...QuickViewCreatorsV2_quickViewDisplay
        }
        ...QuickViewCategory_item
    }
`;

const messages = defineMessages({
    label: {
        id: 'dibs-buyer-product-tile.QuickView.closeLabel',
        defaultMessage: 'Close Quick View',
    },
    viewDetails: {
        id: 'dibs-buyer-product-tile.QuickView.viewDetails',
        defaultMessage: 'View Full Details',
    },
});

type Props = {
    itemSearch: QuickView_itemSearch$key | null | undefined;
    item: QuickView_item$key;
    buyerHost?: string;
    index: number;
    size: TileSize;
    onViewDetailsClick?: () => void;
};

export const QuickView: FC<Props> = ({
    itemSearch: itemSearchRef,
    index,
    size,
    onViewDetailsClick,
    item: itemRef,
    buyerHost,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const item = useFragment(itemFragment, itemRef);
    const { activeQuickView, closeQuickView } = useQuickView();
    const { formatMessage } = useIntl();
    const isSmall = size === 'small';

    const { title, linkData, quickViewDisplay } = item || {};
    const quickViewLinkData = {
        path: withBuyerHost(linkData?.path, buyerHost),
        isLinkable: false,
    };

    const paragraphsMap = (quickViewDisplay?.paragraphs || []).reduce<
        Record<string, string | null>
    >((map, paragraph) => {
        if (!paragraph) {
            return map;
        }
        const { key, text, mobileText } = paragraph;
        if (!key) {
            return map;
        }
        map[key] = isSmall ? mobileText : text;
        return map;
    }, {});

    const { location, description, materials, attributes } = paragraphsMap;

    return (
        <div
            className={classnames(
                dibsCss.absolute,
                dibsCss.zLow,
                dibsCss.inset0,
                dibsCss.bgWhite,
                activeQuickView === index ? dibsCss.flex : dibsCss.hidden,
                dibsCss.flexCol,
                dibsCss.borderDolphin,
                dibsCss.border,
                dibsCss.borderSolid,
                isSmall
                    ? [dibsCss.pxXsmall, dibsCss.ptLarger, dibsCss.pbXsmall]
                    : [dibsCss.plSmall, dibsCss.prXsmall, dibsCss.ptXlarge, dibsCss.pbSmall]
            )}
        >
            <div className={classnames(dibsCss.overflowAuto, dibsCss.flex1)}>
                {!isSmall && (
                    <p
                        data-tn="quick-view-title"
                        className={classnames(dibsCss.sassyFontBodySmallHeavy, dibsCss.mbXxsmall)}
                    >
                        {title}
                    </p>
                )}
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        isSmall
                            ? [dibsCss.gapXsmall, dibsCss.sassyFontBodyXSmall]
                            : [dibsCss.gapSmaller, dibsCss.sassyFontBodySmall]
                    )}
                >
                    <div className={dibsCss.sassyFontBodyXSmall}>
                        <QuickViewCreators
                            quickViewDisplay={quickViewDisplay}
                            buyerHost={buyerHost}
                        />
                        {location && (
                            <p data-tn="quick-view-location">
                                <FormattedMessage
                                    id="dibs-buyer-product-tile.QuickView.location"
                                    defaultMessage="Located in {location}"
                                    values={{ location }}
                                />
                            </p>
                        )}
                    </div>

                    {description && (
                        <p
                            data-tn="quick-view-description"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                    <QuickViewCategory
                        item={item}
                        itemSearch={itemSearch}
                        attributes={attributes}
                        isSmall={isSmall}
                    />
                    {materials && (
                        <div data-tn="quick-view-materials">
                            <span
                                className={
                                    isSmall
                                        ? dibsCss.sassyFontBodyXSmallHeavy
                                        : dibsCss.sassyFontBodySmallHeavy
                                }
                            >
                                <FormattedMessage
                                    id="dibs-buyer-product-tile.QuickView.materials"
                                    defaultMessage="Materials"
                                />
                            </span>
                            <p>{materials}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className={isSmall ? dibsCss.ptXsmall : dibsCss.ptXsmall}>
                {isSmall ? (
                    <SeoLink
                        linkData={quickViewLinkData}
                        onClick={onViewDetailsClick}
                        dataTn="quick-view-item-link"
                    >
                        {formatMessage(messages.viewDetails)}
                    </SeoLink>
                ) : (
                    <SeoButtonComponent
                        type="secondary"
                        size="small"
                        fullWidth
                        linkData={quickViewLinkData}
                        onClick={onViewDetailsClick}
                        dataTn="quick-view-item-button"
                    >
                        {formatMessage(messages.viewDetails)}
                    </SeoButtonComponent>
                )}
            </div>
            <Link
                className={classnames(dibsCss.absolute, dibsCss.topXsmall, dibsCss.rightXsmall)}
                onClick={() => closeQuickView?.()}
                dataTn="quick-view-close"
                ariaLabel={formatMessage(messages.label)}
            >
                <Close variation="24" />
            </Link>
        </div>
    );
};
